import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { Skeleton } from '@mui/lab';
import { TextField, Button, MenuItem, Card, Tabs, Tab, DialogProps } from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { Modal } from '../../../components/other/Modals';
import UsersBuyerTable from '../../../components/tableComponents/Table/UsersBuyerTable';
import { IAppState } from '../../../store/rootDuck';
import { actions as orderActions } from '../../../store/ducks/orders.duck';
import { actions as usersActions } from '../../../store/ducks/users.duck';
import { actions as cartActions } from '../../../store/ducks/cart.duck';
import { actions as locationActions } from '../../../store/ducks/yaLocations.duck';
import { IOrder } from '../../../interfaces/order';
import homeStyles from '../../../constants/homeStyles';
import ButtonWithLoader from '../../../components/other/Buttons/ButtonWithLoader';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { getOrderTypeWithTranslates } from './constatns';
import { UserListType } from '../users/constants';
import { useStyles } from './hooks/useStyles';
import AlertDialog from '../../../components/other/Dialog/AlertDialog';
import { TabPanel } from '../../../components/other/Tab/TabPanel';
import AutocompleteCopy from '../../../components/formComponents/AutocompleteLocationsCopy';
import OrderTable from './components/OrderTable';
import { useDefineUserRole } from '../../../hooks';
import AutocompleteLocations from '../../../components/AutocompleteLocations';
import {
  selectYalocations,
  selectLoadingYalocations,
  selectLoadingByIdYalocaitons,
} from '../cart/selectors';
import { useAxiosGetCallback } from '../../../hooks/useAxiosGet';
import { useHandleCountrySelect } from '../../auth/hooks/useHandleCountrySelect';
import { CompanyBuyer } from './components/CompanyBuyer';

const getInitialValues = (order: IOrder) => {
  const values: any = {
    location: {
      id: 1,
      address: order.address || '',
      lng: 0,
      lat: 0,
      city: '',
      country: '',
      house: '',
      province: '',
      street: '',
      working_times: undefined,
    },
    status: order.status || 'payed',
    payment_status: order.payment_status || 'not_paid',
  };
  return values;
};

export enum ViewMode {
  VIEW = 'view',
  EDIT = 'edit',
}

const OrderPage: React.FC<TPropsFromRedux> = ({
  order,
  loading,
  editLoading,
  fetchById,
  edit,
  editSuccess,
  clear,
  editError,
  loadingUsers,
  perPageUsers,
  pageUsers,
  totalUsers,
  users,
  fetchUsers,
  filter,
}) => {
  const dispatch = useDispatch();
  const { me } = useSelector(({ profile }: IAppState) => profile);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const locations = useSelector(selectYalocations);
  const loadingLocations = useSelector(selectLoadingYalocations);
  const byCoordsLoading = useSelector(selectLoadingByIdYalocaitons);
  const [buyerID, setBuyerId] = useState<number | undefined>(order?.user.id);
  const isVendor = useDefineUserRole(me, ['ROLE_VENDOR', 'ROLE_VENDOR_STAFF']);
  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  const [selectedId, setSelectedId] = useState(-1);
  const isMainOrder = !order?.main_order;
  const { data: countries, makeRequest } = useAxiosGetCallback<any>();
  const { setCountryId, countryData } = useHandleCountrySelect(countries, 1);

  const [valueTabs, setValueTabs] = useState(0);
  const intl = useIntl();
  const navigate = useNavigate();
  const { id, mode } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const homeClasses = homeStyles();
  const classes1 = useStyles();
  const { classes } = classes1;

  useEffect(() => {
    if (id) {
      fetchById(+id);
    }
  }, [id, fetchById]);

  useEffect(() => {
    if (order?.buyer_company?.country?.id) {
      setCountryId(order?.buyer_company?.country?.id);
    }
  }, [order]);

  useEffect(() => {
    if (editSuccess || editError) {
      enqueueSnackbar(
        editSuccess
          ? ` ${intl.formatMessage({
              id: 'ORDER.SNACKBAR.EDIT',
            })}`
          : `${intl.formatMessage({ id: 'PRODUCT.SNACKBAR.ERROR' })} ${editError}`,
        { variant: editSuccess ? 'success' : 'error' }
      );
      if (editSuccess) {
        // history.goBack();
        id && fetchById(+id);
      }
    }
  }, [editSuccess, editError, enqueueSnackbar, id, intl]);

  const {
    values,
    handleBlur,
    handleChange,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    isValid,
    setValues,
  } = useFormik({
    initialValues: order ? getInitialValues(order) : {},
    enableReinitialize: true,
    validationSchema: Yup.object().shape({}),
    onSubmit: submitValues => {
      id &&
        edit({
          id: +id,
          data: {
            ...submitValues,
            user_id: buyerID,
            location: undefined,
            address: submitValues.location.address,
          },
        });
    },
  });

  useEffect(() => {
    if (order) {
      setValues(getInitialValues(order));
    }
  }, [order, setValues]);

  const translates: { [type: string]: string } = {
    cart: intl.formatMessage({ id: 'ORDER.TYPE.CART' }),
    new: intl.formatMessage({ id: 'ORDER.TYPE.NEW' }),
    formation: intl.formatMessage({ id: 'ORDER.TYPE.FORMATION' }),
    ready_to_delivery: intl.formatMessage({ id: 'ORDER.TYPE.DELIVERY' }),
    payed: intl.formatMessage({ id: 'ORDER.TYPE.PAYED' }),
    canceled: intl.formatMessage({ id: 'ORDER.TYPE.CANCELED' }),
    completed: intl.formatMessage({ id: 'ORDER.TYPE.COMPLETED' }),
    in_processing: intl.formatMessage({ id: 'ORDER.TYPE.PROCCESS' }),
  };
  const orderTypes = getOrderTypeWithTranslates(translates);
  const disabled = mode === ViewMode.VIEW;

  if (me && !me.is_admin && mode !== ViewMode.VIEW) {
    navigate(-1);
  }

  setLayoutSubheader({
    title:
      mode === ViewMode.VIEW
        ? `${intl.formatMessage({ id: 'ORDER.HEADER.VIEW' })}`
        : `${intl.formatMessage({ id: 'ORDER.HEADER.EDIT' })}`,
  });
  setLayoutFooter({ show: true });

  const submitCallback = useCallback(() => {
    if (
      order &&
      (order.status === 'payed' ||
        order.status === 'completed' ||
        order.status === 'ready_to_delivery')
    ) {
      setAlertOpen(true);
    } else {
      handleSubmit();
    }
  }, [order, handleSubmit]);

  useEffect(() => {
    makeRequest('/api/phone_codes');
  }, []);

  useEffect(
    () => () => {
      clear();
    },
    [clear]
  );

  const handleConnectUser = useCallback(
    (Id: number) => {
      setBuyerId(Id);
      handleSubmit();
      setOpenUsersDialog(false);
    },
    [buyerID]
  );

  const handleTabsChange = (event: any, newValue: number) => {
    setValueTabs(newValue);
  };

  const selectAction = useCallback(
    (id: number) => {
      setSelectedId(id);
    },
    [setSelectedId]
  );

  const alertText = `${intl.formatMessage({ id: 'ORDER.ALERT.EDIT.TEXT1' })} "${
    translates[order?.status || '']
  }". ${intl.formatMessage({ id: 'ORDER.ALERT.EDIT.TEXT2' })}`;

  if (loading) {
    return <></>;
  }

  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message={alertText}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => handleSubmit()}
      />
      <Card className={homeClasses.classes.container}>
        <Tabs
          value={valueTabs}
          onChange={handleTabsChange}
          variant='scrollable'
          indicatorColor='primary'
          textColor='primary'
          aria-label='tabs'
        >
          <Tab label={intl.formatMessage({ id: 'ORDER.VIEW.TABS.FIELDS' })} />
          <Tab label={intl.formatMessage({ id: 'ORDER.VIEW.TABS.ITEMS' })} />
          {me?.is_admin && <Tab label={intl.formatMessage({ id: 'COMPANY.ROLES.BUYER' })} />}
        </Tabs>
        <div className={homeClasses.classes.table}>
          <TabPanel value={valueTabs} index={0}>
            {loading ? (
              <>
                <Skeleton width='100%' height={70} animation='wave' />
                <Skeleton width='100%' height={70} animation='wave' />
                <Skeleton width='100%' height={70} animation='wave' />
                <Skeleton width='100%' height={70} animation='wave' />
                <Skeleton width='100%' height={70} animation='wave' />
                <Skeleton width='100%' height={70} animation='wave' />
                <Skeleton width='100%' height={70} animation='wave' />
              </>
            ) : (
              <div className={classes.fieldsWrapper}>
                {isMainOrder && (
                  <TextField
                    select
                    margin='normal'
                    label={`${intl.formatMessage({ id: 'ORDER.INPUT.STATUS' })}`}
                    value={values.status || ''}
                    onChange={handleChange}
                    name='status'
                    variant='outlined'
                    helperText={touched.status && errors.status?.toString()}
                    error={Boolean(touched.status && errors.status)}
                    disabled={disabled}
                    className={classes.textField}
                    fullWidth
                  >
                    {orderTypes.map(item => (
                      <MenuItem value={item.type}>{item.translate}</MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  select
                  margin='normal'
                  label={intl.formatMessage({ id: 'ORDER.PAYMENT.STATUS' })}
                  value={values.payment_status || ''}
                  onChange={handleChange}
                  name='payment_status'
                  variant='outlined'
                  disabled={disabled}
                  className={classes.textField}
                  fullWidth
                >
                  <MenuItem value='paid'>
                    {intl.formatMessage({ id: 'ORDER.PAYMENT.STATUS.PAID' })}
                  </MenuItem>
                  <MenuItem value='not_paid'>
                    {intl.formatMessage({ id: 'ORDER.PAYMENT.STATUS.NOT_PAID' })}
                  </MenuItem>
                </TextField>
                {isMainOrder && (
                  <div className={classes.textField}>
                    {disabled ? (
                      <AutocompleteCopy
                        error={Boolean(touched.address && errors.address)}
                        value={{ address: values.location?.address }}
                        setSelectedLocation={location => {
                          setFieldValue('address', { address: location.address });
                        }}
                        disabled
                      />
                    ) : (
                      <AutocompleteLocations
                        options={locations || []}
                        loading={loadingLocations || byCoordsLoading}
                        inputValue={values.location || ''}
                        editable
                        label={intl.formatMessage({
                          id: 'STORE.INPUT.LOCATION',
                        })}
                        inputClassName={classes.autoLoc}
                        fetchLocations={data => dispatch(locationActions.fetchRequest(data))}
                        clearLocations={() => dispatch(locationActions.clear())}
                        setSelectedLocation={location => {
                          location
                            ? setFieldValue('location', location)
                            : setFieldValue('location', {});
                        }}
                        handleBlur={handleBlur}
                        disable={disabled}
                        inputError={false}
                        inputHelperText={undefined}
                      />
                    )}
                  </div>
                )}

                <div className={classes.bottomActions}>
                  {disabled ? (
                    <Button
                      onClick={() => navigate('/orders/list/full')}
                      className={classes.buttons}
                      variant='contained'
                      color='primary'
                      style={{ backgroundColor: '#88888b' }}
                    >
                      Вернуться к списку заказов
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => navigate('/orders/list/full')}
                        className={classes.buttons}
                        style={{ marginRight: 0, backgroundColor: '#88888b' }}
                        variant='contained'
                        color='primary'
                      >
                        Вернуться к списку заказов
                      </Button>
                      <Button
                        onClick={() => navigate(-1)}
                        className={classes.buttons}
                        style={{ marginRight: 0 }}
                        variant='contained'
                        color='success'
                      >
                        {intl.formatMessage({ id: 'CATEGORY.BUTTON.CANCEL' })}
                      </Button>
                      <ButtonWithLoader
                        disabled={editLoading || !isValid}
                        loading={editLoading}
                        onPress={submitCallback}
                        className={classes.buttons}
                      >
                        {intl.formatMessage({ id: 'ORDER.BUTTON.EDIT' })}
                      </ButtonWithLoader>
                    </>
                  )}
                </div>
              </div>
            )}
          </TabPanel>
          <TabPanel value={valueTabs} index={1}>
            <OrderTable
              orders={order || values}
              isVendor={isVendor}
              selectAction={selectAction}
              selectedId={selectedId}
              me={me}
              isBuyer={!!isBuyer}
            />
            <div className={classes.bottomActions}>
              {disabled ? (
                <Button
                  onClick={() => navigate('/orders/list/full')}
                  className={classes.buttons}
                  style={{ backgroundColor: '#88888b' }}
                  variant='contained'
                  color='primary'
                >
                  Вернуться к списку заказов
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/orders/list/full')}
                    className={classes.buttons}
                    style={{ backgroundColor: '#88888b' }}
                    variant='contained'
                    color='primary'
                  >
                    Вернуться к списку заказов
                  </Button>
                  <Button
                    onClick={() => navigate(-1)}
                    className={classes.buttons}
                    variant='contained'
                    color='success'
                    style={{ marginLeft: 10 }}
                  >
                    {intl.formatMessage({ id: 'CATEGORY.BUTTON.CANCEL' })}
                  </Button>
                  <ButtonWithLoader
                    marginLeft={10}
                    disabled={editLoading || !isValid}
                    loading={editLoading}
                    onPress={submitCallback}
                  >
                    {intl.formatMessage({ id: 'ORDER.BUTTON.EDIT' })}
                  </ButtonWithLoader>
                </>
              )}
            </div>
          </TabPanel>
          <TabPanel value={valueTabs} index={2}>
            <CompanyBuyer order={order} countryData={countryData} />
            <div className={classes.bottomActions}>
              {disabled ? (
                <Button
                  onClick={() => navigate('/orders/list/full')}
                  className={classes.buttons}
                  variant='contained'
                  color='primary'
                  style={{ backgroundColor: '#88888b' }}
                >
                  Вернуться к списку заказов
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/orders/list/full')}
                    className={classes.buttons}
                    variant='contained'
                    color='primary'
                    style={{ backgroundColor: '#88888b' }}
                  >
                    Вернуться к списку заказов
                  </Button>
                  <Button
                    onClick={() => navigate(-1)}
                    className={classes.buttons}
                    variant='contained'
                    color='success'
                    style={{ marginLeft: 10 }}
                  >
                    {intl.formatMessage({ id: 'CATEGORY.BUTTON.CANCEL' })}
                  </Button>
                  <ButtonWithLoader
                    marginLeft={10}
                    disabled={editLoading || !isValid}
                    loading={editLoading}
                    onPress={submitCallback}
                  >
                    {intl.formatMessage({ id: 'ORDER.BUTTON.EDIT' })}
                  </ButtonWithLoader>
                </>
              )}
            </div>
          </TabPanel>
        </div>
      </Card>
      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openUsersDialog}
        onClose={() => setOpenUsersDialog(false)}
        title={intl.formatMessage({ id: 'COMPANY.EDIT.BUYER_USER_TITLE' })}
        loading={loadingUsers}
        content={
          <UsersBuyerTable
            loading={loadingUsers}
            paginationData={{ page: pageUsers, perPage: perPageUsers, total: totalUsers }}
            onEdit={userId => navigate(`/users/edit/${UserListType.MANAGER}/${userId}`)}
            buyerID={buyerID}
            onConnect={handleConnectUser}
            // onConnectOff={userId => handleUnConnectUserDialog(userId)}
            users={users}
            fetch={({ page, perPage }) =>
              fetchUsers({ per_page: perPage, page, roles: 'ROLE_BUYER', filter })
            }
          />
        }
        actions={[
          {
            title: intl.formatMessage({ id: 'COMMON.BUTTON.CLOSE' }),
            onClick: () => setOpenUsersDialog(false),
          },
        ]}
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    me: state.profile.me,
    user: state.users.user,
    users: state.users.users,
    loadingUsers: state.users.loading,
    perPageUsers: state.users.per_page,
    pageUsers: state.users.page,
    totalUsers: state.users.total,
    order: state.orders.order,
    loading: state.orders.byIdLoading,
    editError: state.orders.editError,
    editSuccess: state.orders.editSuccess,
    editLoading: state.orders.editLoading,
    filter: state.users.filter,
    error: state.products.error,
    userRole: state.auth.user?.roles,
  }),
  {
    fetchById: orderActions.fetchByIdRequest,
    edit: orderActions.editRequest,
    clear: orderActions.clearEdit,
    fetchUsers: usersActions.fetchRequest,
    addProductToCart: cartActions.multiAddProductRequest,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(OrderPage);
